import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useCallback,
  useRef,
} from "react";
import { createEditor, Editor, Transforms, Range, Node } from "slate";
import { Slate, Editable, withReact, ReactEditor } from "slate-react";
import { withHistory } from "slate-history";
import studyReportContext from "../../../../context/studyreport/StudyReportContext";
import { NONXRAYMODALITIES } from "../../../../constants/Constants";
import ImageIndicator from "../../../../components/SVGComponents/ImageIndicator";
import SendChatInputIcon from "../../../../components/SVGComponents/SendChatInputIcon";
import CustomToolTipHost from "../../../../components/CustomToolTipHost/CustomToolTipHost";
import { DirectionalHint } from "@fluentui/react";
import {
  isImageAllowedToLLM,
  slateJsonToPlainText,
} from "../../../../helpers/CommonUtility";
import VoiceInputControl from "../VoiceAssistant/VoiceInputControl";
import VapiAssistantButton from "../VoiceAssistant/VapiAssistantButton";
import StenographDisplay from "../VoiceAssistant/StenographDisplay";
import AgenticReportingContext from "../../../../context/agentic-reporting/AgenticReportingContext";
import studyviewerContext from "../../../../context/studyviewer/StudyviewerContext";
import { isMobile } from "react-device-detect";

const textToSlate = (text = "") => {
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed;
  } catch {}
  const paragraphs = text.trim().split(/\n+/).filter(Boolean);
  if (paragraphs.length === 0) {
    return [{ type: "paragraph", children: [{ text: "" }] }];
  }
  return paragraphs.map((p) => ({
    type: "paragraph",
    children: [{ text: p }],
  }));
};

const withMentions = (editor) => {
  const { isInline, isVoid } = editor;
  editor.isInline = (element) =>
    element.type === "mention" ? true : isInline(element);
  editor.isVoid = (element) =>
    element.type === "mention" ? true : isVoid(element);
  return editor;
};

const baseBorderStyle = {
  border: "1px solid #BDBDBD",
  backgroundColor: "white",
  boxSizing: "border-box",
};
const fixedSectionHeight = "46px";

const ChatInput = ({ onHeightChange, reportMenu, onSend }) => {
  const mentionPopupRef = useRef();

  const [slateKey, setSlateKey] = useState(Date.now());

  const [isMultiLine, setIsMultiLine] = useState(false);
  const [targetRange, setTargetRange] = useState(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionSearch, setMentionSearch] = useState("");
  const isSubmittingRef = useRef(false);

  const {
    setActiveField,
    findingSentence,
    isExternal,
    reportDoneStatusState,
    llmReportTitle,
    modality,
    maskImages,
    handleModelChange,
    allReportTemplates,
    studyDescription,
    setModel,
    isWidget,
    patientReports,
    activeTabKey,
    showPathologySentences,
  } = useContext(studyReportContext);

  const {
    vapiFunctions,
    inputValue,
    setInputValue,
    firstAgenticLLMReport,
    isFirstCall,
    registerChatInputRef,
    handleDSRRegenerate,
    isVapiStenographActive,
    isTalking,
    isAgenticTab,
    agenticReportAgents,
    setSelectedReportMention,
    selectedReportMention,
    getAvailableAgents,
    hasAutoPopulated, 
    setHasAutoPopulated,
    setReportFromAPI,
    isMicRecording
  } = useContext(AgenticReportingContext);

  const { cxrVersion, showFindingsHighlighted, showProbablilityScores } =
    useContext(studyviewerContext) || {};

  const editor = useMemo(
    () => withMentions(withHistory(withReact(createEditor()))),
    [activeTabKey],
  );

  const activeTabGlobal = patientReports?.find((r) => r.key === activeTabKey);
  const isExistingReportGlobal = activeTabGlobal?.reportDetails && "reportTemplateId" in activeTabGlobal.reportDetails;
  const shouldSkipAutoPopulateGlobal = reportDoneStatusState > 0 && isExistingReportGlobal;

  const [slateValue, setSlateValue] = useState(() =>
    textToSlate(inputValue || ""),
  );
  const isInputEmpty = useMemo(
    () => slateJsonToPlainText(inputValue).trim() === "",
    [inputValue],
  );

  useEffect(() => {
    setSlateValue(textToSlate(inputValue || ""));
    setSlateKey(Date.now());
    setTargetRange(null);
  }, [activeTabKey]);

  useEffect(() => {
    if (inputValue === "") {
      setSlateValue([{ type: "paragraph", children: [{ text: "" }] }]);
      setSlateKey(Date.now());
    }
  }, [inputValue]);

  useEffect(() => {
    if (registerChatInputRef && activeTabKey) {
      registerChatInputRef(activeTabKey, editor);
    }
  }, [registerChatInputRef, editor, activeTabKey]);

  // const filteredAgents = useMemo(() => {
  //   const list = agenticReportAgents || [];
  //   if (!mentionSearch) return list;
  //   return list.filter((a) =>
  //     a.label.toLowerCase().includes(mentionSearch.toLowerCase()),
  //   );
  // }, [mentionSearch, agenticReportAgents]);

  const filteredAgents = useMemo(() => {
    const list = getAvailableAgents ? getAvailableAgents() : [];
    if (!mentionSearch) return list;
    return list.filter((a) =>
      a.label.toLowerCase().includes(mentionSearch.toLowerCase()),
    );
  }, [mentionSearch, getAvailableAgents]);

  useEffect(() => {
    const handleVapiShortcut = (event) => {
      if (event.ctrlKey && (event.code === "Space" || event.key === " ")) {
        console.log("Ctrl + Space Detected");
        if (
          (reportMenu || isExternal || isWidget) &&
          isAgenticTab &&
          vapiFunctions
        ) {
          event.preventDefault();
          event.stopPropagation();

          if (isTalking) {
            vapiFunctions.stopAssistant(true);
          } else {
              if (!isMicRecording) {
                  vapiFunctions.startAssistant();
              }
          }
        }
      }
    };

    window.addEventListener("keydown", handleVapiShortcut, true);

    return () => {
      window.removeEventListener("keydown", handleVapiShortcut, true);
    };
  }, [
    reportMenu,
    isExternal,
    isWidget,
    isAgenticTab,
    isTalking,
    vapiFunctions,
    isMicRecording
  ]);

  useEffect(() => {
    const el = mentionPopupRef.current;
    if (targetRange && el) {
      const isValidRange = 
        targetRange.focus && 
        Node.has(editor, targetRange.focus.path);

      if (!isValidRange) {
        setTargetRange(null);
        return;
      }
      try {
        const domRange = ReactEditor.toDOMRange(editor, targetRange);
        const rect = domRange.getBoundingClientRect();
        const parentContainer = el.closest('[style*="position: relative"]');
        if (!parentContainer) return;
        const parentRect = parentContainer.getBoundingClientRect();

        el.style.top = `${rect.top - parentRect.top - el.offsetHeight - 3}px`;
        el.style.left = `${rect.left - parentRect.left}px`;
      } catch (e) {
        console.warn("Mention positioning failed, resetting range.", e);
        setTargetRange(null);
      }
    }
  }, [mentionSearch, targetRange, editor, filteredAgents]);

  useEffect(() => {
    if (onHeightChange) {
      try {
        const editorEl = ReactEditor.toDOMNode(editor, editor);
        if (editorEl) {
          const lineHeight = 20;
          const maxLines = 3;
          const maxHeight = lineHeight * maxLines;
          const minHeight = 36;

          const numLines = Math.max(1, Math.min(maxLines, Math.floor(editorEl.scrollHeight / lineHeight)));
          const newHeight = numLines === 1 ? minHeight : numLines * lineHeight;

          onHeightChange(newHeight);

          if (editorEl.scrollHeight > maxHeight) {
            editorEl.style.overflowY = "auto";
          } else {
            editorEl.style.overflowY = "hidden";
          }
          setIsMultiLine((prevIsMultiLine) => {
            if (numLines >= 3) {
              return true;
            }

            if (numLines <= 1) {
              return false;
            }

            return prevIsMultiLine;
          });
        }
      } catch {}
    }
  }, [slateValue, onHeightChange, editor]);

  useEffect(() => {
    if (!isFirstCall) return;

    const activeTab = patientReports?.find((r) => r.key === activeTabKey);
    if (!activeTab) return;

    const templateTitle = activeTab.reportDetails?.reportTemplateTitle;
    const isAutoSelectedTab =
      activeTab.reportDetails?.reportTemplateTitle === llmReportTitle;
    const isAgenticReportTab =
      activeTab && activeTab.text.includes("Agentic Report");

    if (
      !hasAutoPopulated &&
      !shouldSkipAutoPopulateGlobal &&
      isInputEmpty &&
      llmReportTitle &&
      firstAgenticLLMReport === "" &&
      isAgenticReportTab
    ) {
      const matchingAgent = agenticReportAgents?.find(
        (a) => a.label.toLowerCase() === templateTitle?.toLowerCase(),
      );
      const mentionTitle = matchingAgent ? matchingAgent.label : llmReportTitle;
      const mentionValue = matchingAgent ? matchingAgent.value : null;

      const isAnalyzerModeWithoutAI = !showPathologySentences;

      if (mentionTitle) {
        const initialNodes = [
          {
            type: "paragraph",
            children: [
              { text: "Create a " },
              {
                type: "mention",
                character: mentionTitle,
                value: mentionValue,
                children: [{ text: "" }],
              },
              { text: " report." },
            ],
          },
          ...(isAutoSelectedTab && findingSentence?.trim() && !isAnalyzerModeWithoutAI
            ? [
                {
                  type: "paragraph",
                  children: [{ text: findingSentence.trim() }],
                },
              ]
            : []),
        ];
        setSlateValue(initialNodes);
        const serializedValue = JSON.stringify(initialNodes);
        setInputValue(serializedValue);
        setSlateKey(Date.now());
        setHasAutoPopulated(true);
        setSelectedReportMention({ label: mentionTitle, value: mentionValue });
      }
    }
  }, [
    isFirstCall,
    hasAutoPopulated,
    activeTabKey,
    patientReports,
    reportDoneStatusState,
    llmReportTitle,
    findingSentence,
    firstAgenticLLMReport,
    agenticReportAgents,
    setHasAutoPopulated,
    setInputValue,
    isInputEmpty,
    shouldSkipAutoPopulateGlobal,
    cxrVersion,
    showFindingsHighlighted,
    showProbablilityScores
  ]);

  const removeAllMentions = useCallback(() => {
    const entries = Array.from(
      Editor.nodes(editor, { at: [], match: (n) => n.type === "mention" }),
    );
    for (const [, path] of entries) {
      Transforms.removeNodes(editor, { at: path });
    }
  }, [editor]);

  const insertMention = useCallback(
    (agent) => {
      const mention = {
        type: "mention",
        character: agent.label,
        value: agent.value,
        children: [{ text: "" }],
      };
      console.log(allReportTemplates.agenticReportTemplates);
      if (targetRange) {
        try {
          Transforms.select(editor, targetRange);
          Transforms.delete(editor);
        } catch (e) {}
      }

      removeAllMentions();

      Transforms.insertNodes(editor, mention);
      Transforms.move(editor);
      Transforms.move(editor);

      if (handleModelChange && allReportTemplates?.agenticReportTemplates) {
        if (reportDoneStatusState === 0) {
          const matchingTemplate = allReportTemplates.agenticReportTemplates.find(
            (item) => item.agenticReportTemplateDto?.title === agent.label,
          );

          if (matchingTemplate) {
            const reportTemplate =
              matchingTemplate.agenticReportTemplateDto.template;
            setModel(reportTemplate);
            setReportFromAPI(reportTemplate);
          } else {
            setModel("");
            setReportFromAPI("");
          }
        }
      }

      setTargetRange(null);
      setMentionSearch("");
    },
    [editor, targetRange, removeAllMentions],
  );

  const isTextareaDisabled =
    llmReportTitle === "" ||
    (isInputEmpty && Object.values(maskImages).length === 0) ||
    isTalking;

  const handleSendMessage = () => {
    if (isTextareaDisabled) return;

    let mentionData = null;
    const [mentionEntry] = Editor.nodes(editor, {
      at: [],
      match: (n) => n.type === "mention",
    });

    if (mentionEntry) {
      const [node] = mentionEntry;
      mentionData = { label: node.character, value: node.value };
    } else if (selectedReportMention) {
      mentionData = selectedReportMention;
    } else {
      mentionData = null;
    }

    isSubmittingRef.current = true;
    setSelectedReportMention(mentionData);
    handleDSRRegenerate(mentionData).finally(() => {
      isSubmittingRef.current = false;
    });
    setInputValue("");
    Transforms.deselect(editor);
    setSlateValue([{ type: "paragraph", children: [{ text: "" }] }]);
    setSlateKey(Date.now());
    if (onSend) onSend();
  };

  function getToolTipContent() {
    if (isTalking) return "You are in voice mode";
    if (!isTextareaDisabled) return "Send";
    const noTitle = llmReportTitle === "";
    const noPrompt = isInputEmpty;
    const noImage = Object.values(maskImages).length === 0;
    const needsImage = !isExternal && noImage;
    // if (NONXRAYMODALITIES.includes(modality)) {
    //   if (noTitle && noPrompt && needsImage)
    //     return "Missing title, prompt, or image";
    //   if (noTitle) return "Title is not selected";
    //   if (noPrompt && needsImage) return "Prompt or image are missing";
    //   if (noPrompt) return "Prompt is missing";
    //   if (needsImage) return "Image is missing";
    // } else {
    if (noTitle && noPrompt) return "Missing Title and prompt";
    if (noTitle) return "Title is not selected";
    if (noPrompt) return "Prompt is missing";
    // }
    return "Required information is missing";
  }

  const handleKeyDown = (event) => {
    if (targetRange && filteredAgents.length > 0) {
      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setMentionIndex((p) => (p >= filteredAgents.length - 1 ? 0 : p + 1));
          break;
        case "ArrowUp":
          event.preventDefault();
          setMentionIndex((p) => (p <= 0 ? filteredAgents.length - 1 : p - 1));
          break;
        case "Tab":
        case "Enter":
          event.preventDefault();
          insertMention(filteredAgents[mentionIndex]);
          break;
        case "Escape":
          event.preventDefault();
          setTargetRange(null);
          break;
      }
      event.stopPropagation();
      return;
    }

    if (event.key === "Enter" && event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
      event.stopPropagation();
      return;
    }

    const blockedKeys = [
      "s",
      "r",
      "i",
      "h",
      "v",
      "l",
      "e",
      "0",
      "1",
      "2",
      "3",
      "4",
      "5",
      "6",
      "@",
      "Backspace",
      "Delete",
      "ArrowUp",
      "ArrowDown",
      "ArrowLeft",
      "ArrowRight",
    ];

    if (
      blockedKeys.includes(event.key) ||
      blockedKeys.includes(event.key.toLowerCase())
    ) {
      event.stopPropagation();
    }

    if (event.ctrlKey && event.key.toLowerCase() === "v") {
      event.stopPropagation();
    }
  };

  const handleSlateChange = (newSlateValue) => {
    setSlateValue(newSlateValue);

    const serializedValue = JSON.stringify(newSlateValue);
    if (inputValue !== serializedValue) {
      setInputValue(serializedValue);
    }

    const [mentionEntry] = Editor.nodes(editor, {
      at: [],
      match: (n) => n.type === "mention",
    });

    if (mentionEntry) {
      const [node] = mentionEntry;
      const mentionData = { label: node.character, value: node.value };
      if (
        !selectedReportMention ||
        selectedReportMention.label !== mentionData.label ||
        selectedReportMention.value !== mentionData.value
      ) {
        setSelectedReportMention(mentionData);
      }
      setTargetRange(null);
      setMentionSearch("");
      return;
    }

    if (isFirstCall && selectedReportMention && !isSubmittingRef.current) {
      setSelectedReportMention(null);
    }

    const { selection } = editor;
    if (selection && Range.isCollapsed(selection)) {
      const [start] = Range.edges(selection);

      const blockEntry = Editor.above(editor, {
        match: (n) => Editor.isBlock(editor, n),
      });
      if (blockEntry) {
        const [, blockPath] = blockEntry;
        const blockStart = Editor.start(editor, blockPath);
        const toCursorRange = Editor.range(editor, blockStart, start);
        const toCursorText = Editor.string(editor, toCursorRange);

        const match = toCursorText.match(/@([^\n@]*)$/);

        if (match) {
          const token = match[1] ?? "";
          const mentionStart = Editor.before(editor, start, {
            unit: "character",
            distance: token.length + 1,
          });
          if (mentionStart) {
            const mentionRange = Editor.range(editor, mentionStart, start);
            setTargetRange(mentionRange);
            setMentionSearch(token);
            setMentionIndex(0);
            return;
          }
        }
      }
    }

    setTargetRange(null);
    setMentionSearch("");
  };

  const MentionElement = ({ attributes, children, element }) => {
    const onClick = (e) => {
      e.preventDefault();
      const path = ReactEditor.findPath(editor, element);
      const range = Editor.range(editor, path);
      Transforms.select(editor, range);
      setTargetRange(range);
      setMentionSearch("");
      setMentionIndex(0);
    };
    return (
      <span
        {...attributes}
        contentEditable={false}
        onClick={onClick}
        style={{
          padding: "1px 4px",
          margin: "0 2px",
          backgroundColor: "#e6f7ff",
          fontSize: "0.9em",
          cursor: "pointer",
        }}
      >
        @{element.character}
        {children}
      </span>
    );
  };

  const renderElement = useCallback(
    (props) => {
      switch (props.element.type) {
        case "mention":
          return <MentionElement {...props} />;
        default:
          return <p {...props.attributes}>{props.children}</p>;
      }
    },
    [editor],
  );

  return (
    <>
      <VoiceInputControl />
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          position: "relative",
          backgroundColor: "white",
          padding: "4px 4px",
        }}
      >
        {false && isImageAllowedToLLM(modality, studyDescription) && (
          <div
            style={{
              ...baseBorderStyle,
              display: "flex",
              alignItems: "center",
              paddingRight: "6px",
              borderTopLeftRadius: "4px",
              borderBottomLeftRadius: "4px",
            }}
          >
            <div style={{ marginLeft: "6px" }}>
              <CustomToolTipHost
                content={
                  !maskImages || Object.values(maskImages).length === 0
                    ? "No image selected"
                    : `${Object.values(maskImages).length} image${Object.values(maskImages).length > 1 ? "s" : ""} selected`
                }
                id="Image Indicator"
                directionalHint={DirectionalHint.topCenter}
              >
                <div style={{ cursor: "default" }} aria-label="Image Indicator">
                  <ImageIndicator
                    isImage={Object.values(maskImages).length > 0}
                  />
                </div>
              </CustomToolTipHost>
            </div>
          </div>
        )}

        {isVapiStenographActive ? (
          <StenographDisplay baseBorderStyle={baseBorderStyle} />
        ) : (
          <>
            <div
              style={{
                ...baseBorderStyle,
                flex: 1,
                borderTopLeftRadius: "4px",
                borderBottomLeftRadius: "4px",
                minHeight: fixedSectionHeight,
                display: "flex",
                alignItems: "center",
                padding: "2px 0 1px 10px",
              }}
            >
              <Slate
                editor={editor}
                initialValue={slateValue}
                onChange={handleSlateChange}
                key={slateKey}
              >
                <Editable
                  renderElement={renderElement}
                  onKeyDown={handleKeyDown}
                  onFocus={() => setActiveField("chatInput")}
                  onBlur={() => setActiveField(null)}
                  placeholder={shouldSkipAutoPopulateGlobal ? "Please enter your update report instructions" : "Add your Input here..."}
                  style={{
                    fontSize: "13px",
                    fontFamily: "Segoe UI",
                    lineHeight: "20px",
                    padding: "0 10px 0 0",
                    border: "none",
                    outline: "none",
                    flex: 1,
                    background: "transparent",
                    resize: "none",
                    boxSizing: "border-box",
                    color: "black",
                    minHeight: "36px",
                    maxHeight: "72px",
                    overflowY: "auto",
                    wordBreak: "break-all",
                    minWidth: 0,
                  }}
                />

                {targetRange && filteredAgents.length > 0 && (
                  <div
                    ref={mentionPopupRef}
                    style={{
                      position: "absolute",
                      color: "black",
                      zIndex: 10,
                      background: "white",
                      borderRadius: "4px",
                      boxShadow: "0 1px 5px rgba(0,0,0,.2)",
                      padding: "3px",
                      maxHeight: "200px",
                      overflowY: "auto",
                    }}
                    data-cy="mentions-portal"
                  >
                    {filteredAgents.map((agent, i) => (
                      <div
                        key={agent.value}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          insertMention(agent);
                        }}
                        style={{
                          padding: "4px 8px",
                          cursor: "pointer",
                          background:
                            i === mentionIndex ? "#B4D5FF" : "transparent",
                          borderRadius: "3px",
                        }}
                      >
                        {agent.label}
                      </div>
                    ))}
                  </div>
                )}
              </Slate>
            </div>

            {/* <div style={{ ...baseBorderStyle, height: fixedSectionHeight, display: 'flex', alignItems: 'center', borderLeft: 'none', borderTopRightRadius: '4px', borderBottomRightRadius: '4px' }}> */}
            <div
              style={{
                ...baseBorderStyle,
                display: "flex",
                flexDirection: (isMobile || isMultiLine) ? "column" : "row",
                alignItems: "center",
                borderTopRightRadius: "4px",
                borderBottomRightRadius: "4px",
                padding: (isMobile || isMultiLine) ? "4px 2px" : "0 2px",
                justifyContent: "center",
                gap: isMobile ? "2px" : "0",
                width: "72px",
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  order: 1,
                  marginTop: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <VapiAssistantButton />
              </div>
              <div style={{ order: 2 }}>
                <CustomToolTipHost
                  content={getToolTipContent()}
                  id="Send Button"
                  directionalHint={DirectionalHint.topCenter}
                >
                  <button
                    type="button"
                    aria-label="Send"
                    onClick={handleSendMessage}
                    disabled={isTextareaDisabled}
                    style={{
                      background: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: isTextareaDisabled ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "16px",
                      fontWeight: "bold",
                      width: "30px",
                      height: "25.5px",
                      paddingBottom: "2px",
                      flexShrink: 0,
                    }}
                  >
                    <SendChatInputIcon isSendActive={!isTextareaDisabled} />
                  </button>
                </CustomToolTipHost>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ChatInput;
